import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { CloseButton } from '#src/atoms/buttons/CloseButton/index.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { BodyS } from '#src/atoms/typography/index.tsx';
import { TermsDialog } from '#src/components/conversionCredit/TermsDialog/index.tsx';
import { DiscountBannerWrapper } from '#src/components/referral/DiscountBannerWrapper/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useWalletDrawerContext } from '#src/contexts/walletDrawer/index.tsx';
import { useIsTabletAndAbove } from '#src/hooks/device/useIsTabletAndAbove.ts';
import { useAmountWithConversion } from '#src/hooks/useAmountWithConversion.ts';
import { useConversionCredit } from '#src/hooks/useConversionCredit.ts';
import { LIFECYCLE, useLifecycle } from '#src/hooks/useLifecycle.ts';
import { glossary, marketCredits } from '#src/lib/glossary.ts';
import { MonetaryAmount } from '#src/lib/monetaryAmount.ts';

type Props = {
  sport?: Sport;
  rounded?: boolean;
};

const WalletLink = styled(ButtonBase)`
  text-decoration: underline;
`;
const TermsLink = styled(ButtonBase)`
  color: var(--c-white);
  text-decoration: underline;
`;

export const ConversionCreditBanner = ({ sport }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const { formatDistanceToNow } = useIntlContext();
  const sportContext = useSportContext();
  const { toggleDrawer } = useWalletDrawerContext();
  const { currentUser } = useCurrentUserContext();
  const conversionCredit = useConversionCredit(sport || sportContext.sport);
  const { main } = useAmountWithConversion({
    monetaryAmount: conversionCredit?.maxDiscount || MonetaryAmount.zero,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const { update, lifecycle } = useLifecycle();
  const hideConversionCreditBanner = lifecycle?.hideConversionCreditBanner;

  if (
    currentUser?.userSettings?.hideBalance ||
    hideConversionCreditBanner ||
    !conversionCredit
  )
    return null;

  const { endDate, percentageDiscount } = conversionCredit;

  const timeLeft = formatDistanceToNow(endDate, { addSuffix: false });

  return (
    <DiscountBannerWrapper
      rightNode={
        !!isTabletAndAbove && (
          <Horizontal>
            <CloseButton
              size="small"
              onClose={() => {
                update(LIFECYCLE.hideConversionCreditBanner, true);
              }}
            />
          </Horizontal>
        )
      }
    >
      <BodyS>
        <FormattedMessage
          {...marketCredits.haveDiscountCredits}
          values={{
            maxDiscount: main,
            percentageDiscount,
            timeLeft,
            walletLink: (...chunks) => (
              <WalletLink
                onClick={() => {
                  toggleDrawer();
                }}
              >
                {chunks}
              </WalletLink>
            ),
          }}
        />{' '}
        <TermsLink type="button" onClick={() => setOpenDialog(true)}>
          (
          <FormattedMessage {...glossary.termsApply} />)
        </TermsLink>
      </BodyS>
      <TermsDialog open={!!openDialog} onClose={() => setOpenDialog(false)} />
    </DiscountBannerWrapper>
  );
};
